<template>
  <div class="mask-editor" v-loading="loadingGlobal" element-loading-background="rgba(0, 0, 0, 0.8)"
    :element-loading-text="loadingText">
    <div class="editor-container">
      <div class="image-wrapper">
        <img :src="imageUrl ? imageUrl : ''" ref="image" @load="initCanvas" class="source-image">
        <canvas ref="canvas" @mousedown="startDrawing" @mousemove="draw" @mouseup="stopDrawing"
          @mouseleave="stopDrawing" :style="canvasCursor"></canvas>
      </div>
      <div class="toolbar">
        <el-row>
          <el-tooltip effect="light" content="Reset" placement="right" :hide-after="1500" :disabled="isMobile">
            <el-button icon="el-icon-refresh" @click="clearCanvas"></el-button>
          </el-tooltip>
        </el-row>
        <el-row>
          <el-button v-if="isMobile" icon="el-icon-refresh-left" @click="undo" />
          <el-tooltip v-else effect="light" content="Undo" placement="right" :hide-after="1500" :disabled="isMobile">
            <el-button icon="el-icon-refresh-left" @click="undo"></el-button>
          </el-tooltip>
        </el-row>
        <el-row>
          <el-tooltip effect="light" content="Clear" placement="right" :hide-after="1500" :disabled="isMobile">
            <el-button v-if="isErasing" icon="el-icon-edit" @click="toggleEraser"></el-button>
            <el-button v-else @click="toggleEraser">
              <svg-icon icon-class="eraser-btn" />
            </el-button>
          </el-tooltip>
        </el-row>
        <el-row>
          <el-tooltip effect="light" content="Use brush" placement="right" :hide-after="1500" :disabled="isMobile">
            <el-popover placement="right" width="200" trigger="click" popper-class="wrap-popover">
              <el-button slot="reference" icon="el-icon-brush"></el-button>
              <el-slider v-model="brushSize" :step="1" :min="10" :max="60" :show-tooltip="!isMobile" show-stops>
              </el-slider>
            </el-popover>
          </el-tooltip>
        </el-row>
        <el-row>
          <el-tooltip effect="light" content="Auto Mask" placement="right" :hide-after="1500" :disabled="isMobile">
            <el-popover placement="right" width="200" trigger="click" popper-class="wrap-popover">
              <el-button slot="reference" icon="el-icon-thumb" :loading="loading"></el-button>
              <div class="mask-selection">
                <el-checkbox-group v-model="selectedMasks">
                  <el-checkbox v-for="(url, name) in masks" :key="name" :label="name" class="mask-item">
                    {{ name }}
                  </el-checkbox>
                </el-checkbox-group>
              </div>
            </el-popover>
          </el-tooltip>
        </el-row>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchMaskImages } from "@/api/image";
import { imageUrl } from "@/filters";

export default {
  props: {
    imageUrl: String,
    imageId: Number,
  },
  data() {
    return {
      isMobile: false,
      isDrawing: false,
      isErasing: false,
      ctx: null,
      brushSize: 20,
      imageDimensions: { width: 0, height: 0 },
      scaleFactor: 1,
      history: [],
      displaySize: { width: 0, height: 0 },
      lastX: 0,
      lastY: 0,
      masks: {},
      selectedMasks: [],
      loading: false,
      initializing: false,
      retryCount: 0,
      loadingGlobal: false,
      loadingText: "Loading...",
      autoMaskQueue: [], // 队列，用于存储待执行的任务
      isProcessingAutoMask: false,
      canvasReady: false,
    };
  },
  computed: {
    canvasStyle() {
      return {
        width: `${this.imageDimensions.width}px`,
        height: `${this.imageDimensions.height}px`
      };
    },
    imageStyle() {
      return {
        width: `${this.imageDimensions.width}px`,
        height: `${this.imageDimensions.height}px`
      };
    },
    canvasCursor() {
      const createCursor = (size, isEraser) => {
        const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" 
           width="${size}" 
           height="${size}" 
           viewBox="0 0 ${size} ${size}">
        ${isEraser ?
            `<rect x="0" y="0" width="${size}" height="${size}" fill="#000"/>` :
            `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2}" fill="#000"/>`
          }
      </svg>`;
        return `url('data:image/svg+xml;utf8,${encodeURIComponent(svg)}') ${size / 2} ${size / 2}, auto`;
      };

      return {
        cursor: createCursor(this.brushSize, this.isErasing)
      }
    }
  },
  mounted() {
    this.checkDevice();
    this.resizeObserver = new ResizeObserver(entries => {
      // 添加防抖处理
      if (this.resizeTimer) clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        if (entries[0].contentRect.width > 0) {
          this.initCanvas()
        }
      }, 100) // 100ms防抖间隔
    })
    this.$nextTick(() => {
      const wrapper = this.$el.querySelector('.image-wrapper')
      if (wrapper) this.resizeObserver.observe(wrapper)
    })
  },
  beforeDestroy() {
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    this.resizeObserver.disconnect();
  },
  methods: {
    checkDevice() {
      this.isMobile = 'ontouchstart' in document.documentElement;
      console.log("check device result: isMobile ==", this.isMobile);
    },
    initCanvas() {
      if (this.initializing) return;
      this.initializing = true;
      this.canvasReady = false;

      this.retryCount = 0;

      this.$nextTick(() => {
        const img = this.$refs.image;
        const wrapper = this.$el.querySelector('.image-wrapper');
        if (!wrapper || wrapper.clientWidth === 0) {
          if (this.retryCount < 3) { // 最大重试次数
            this.retryCount++;
            setTimeout(() => {
              this.initCanvas();
            }, 50);
          }
          return;
        }

        // 确保canvas元素存在
        const canvas = this.$refs.canvas;
        if (!canvas) return;

        // 初始化2D上下文
        this.ctx = canvas.getContext('2d');
        if (!this.ctx) {
          console.error('Failed to get 2D context');
          return;
        }

        // 计算显示尺寸
        const displayWidth = wrapper.clientWidth;
        this.scaleFactor = displayWidth / img.naturalWidth;

        // 设置canvas尺寸
        canvas.style.width = `${displayWidth}px`;
        canvas.style.height = `${img.naturalHeight * this.scaleFactor}px`;
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;

        // 初始化画布状态
        this.ctx.fillStyle = 'rgba(0, 0, 0, 0)';
        this.ctx.fillRect(0, 0, canvas.width, canvas.height);
        this.ctx.lineCap = 'round';
        this.ctx.strokeStyle = '#000';

        console.log("finish init canvas");

        this.canvasReady = true;
        this.initializing = false;

        this.processAutoMaskQueue();
      })
    },

    startDrawing(e) {
      if (!this.ctx) return; // 添加安全判断
      this.isDrawing = true;

      const { x, y } = this.getCanvasCoordinates(e);
      this.lastX = x;
      this.lastY = y;

      this.saveState();
    },

    getCanvasCoordinates(e) {
      const canvas = this.$refs.canvas;
      const rect = canvas.getBoundingClientRect();

      // 计算原始坐标
      let clientX, clientY;
      if (e.type.startsWith('touch')) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }

      // 转换为canvas元素坐标系
      let x = clientX - rect.left;
      let y = clientY - rect.top;

      // 转换为实际canvas尺寸坐标系
      const scaleX = canvas.width / canvas.clientWidth;
      const scaleY = canvas.height / canvas.clientHeight;
      x *= scaleX;
      y *= scaleY;

      // 边界约束
      x = Math.max(0, Math.min(x, canvas.width));
      y = Math.max(0, Math.min(y, canvas.height));

      return { x, y };
    },

    draw(e) {
      if (!this.isDrawing || !this.ctx) return;

      const { x, y } = this.getCanvasCoordinates(e);

      if (this.isErasing) {
        const eraseSize = this.brushSize / this.scaleFactor;
        this.ctx.clearRect(
          x - eraseSize / 2,
          y - eraseSize / 2,
          eraseSize,
          eraseSize
        );
      } else {
        this.ctx.lineWidth = this.brushSize / this.scaleFactor;
        this.ctx.strokeStyle = 'rgba(0, 0, 0, 1)';
        this.ctx.lineCap = 'round';

        this.ctx.beginPath();
        this.ctx.moveTo(this.lastX, this.lastY);
        this.ctx.lineTo(x, y);
        this.ctx.stroke();

        this.lastX = x;
        this.lastY = y;
      }
    },

    stopDrawing() {
      this.isDrawing = false;
      this.ctx.beginPath();
    },
    clearCanvas() {
      this.ctx.clearRect(0, 0, this.$refs.canvas.width, this.$refs.canvas.height);
      this.history = [];
      this.unloadAutoMasks();
    },
    undo() {
      if (this.history.length > 1) {
        this.history.pop();
        const img = new Image();
        img.src = this.history[this.history.length - 1];
        img.onload = () => {
          this.ctx.clearRect(0, 0, this.$refs.canvas.width, this.$refs.canvas.height);
          this.ctx.drawImage(img, 0, 0);
        };
      }
    },
    toggleEraser() {
      this.isErasing = !this.isErasing;
    },
    saveState() {
      this.history.push(this.$refs.canvas.toDataURL());
    },
    getMaskData() {
      return this.$refs.canvas.toDataURL('image/png');
    },
    resetCanvas() {
      const canvas = this.$refs.canvas
      if (canvas) {
        canvas.width = 0
        canvas.height = 0
      }
      this.imageDimensions = { width: 0, height: 0 }
    },
    loadAutoMaskOptions() {
      console.log("load auto mask options, image id: ", this.imageId);

      if (!this.imageId) return;
      this.loadingGlobal = true;

      fetchMaskImages(this.imageId).then(response => {
        if (response.success) {
          this.masks = response.result || {};
          this.selectedMasks = Object.keys(this.masks);
        }
      });
    },
    unloadAutoMasks() {
      this.selectedMasks = [];
    },
    async applyAutoMasks(operation) {
      // 将任务添加到队列
      this.autoMaskQueue.push(operation);

      // 如果当前没有任务在处理，则开始处理队列
      if (!this.isProcessingAutoMask && this.canvasReady) {
        await this.processAutoMaskQueue();
      }
    },
    async processAutoMaskQueue() {
      this.isProcessingAutoMask = true;

      while (this.autoMaskQueue.length > 0) {
        const operation = this.autoMaskQueue.shift(); // 取出队列中的第一个任务

        const [action, name] = operation;

        try {
          this.loadingText = "Preparing auto mask...";
          this.loadingGlobal = true;
          if (action === 'add') {
            await this.addAutoMask(name);
          } else {
            await this.removeAutoMask(name);
          }
        } catch (error) {
          console.error('Error applying auto mask:', error);
        } finally {
          this.loadingGlobal = false;
        }
      }

      this.isProcessingAutoMask = false;
    },
    async addAutoMask(maskName) {
      const img = await this.loadMaskImage(this.masks[maskName]);

      // 创建临时canvas处理图像
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = this.$refs.canvas.width;
      tempCanvas.height = this.$refs.canvas.height;
      const ctx = tempCanvas.getContext('2d');

      // 步骤1：绘制原始遮罩
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(img, 0, 0);

      // 步骤2：创建遮罩专用canvas
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = tempCanvas.width;
      maskCanvas.height = tempCanvas.height;
      const maskCtx = maskCanvas.getContext('2d');

      // 提取白色区域（带抗锯齿处理）
      const imageData = ctx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
      const data = imageData.data;
      for (let i = 0; i < data.length; i += 4) {
        const isWhite = data[i] > 250 && data[i + 1] > 250 && data[i + 2] > 250;
        data[i] = 0;     // R
        data[i + 1] = 0;   // G
        data[i + 2] = 0;   // B
        data[i + 3] = isWhite ? 255 : 0; // Alpha
      }
      maskCtx.putImageData(imageData, 0, 0);

      // 步骤3：绘制到主画布
      this.ctx.globalCompositeOperation = 'source-over';
      this.ctx.drawImage(maskCanvas, 0, 0);

      // 重置必要状态
      this.ctx.globalCompositeOperation = 'source-over';
      this.ctx.strokeStyle = '#000';
      this.ctx.lineWidth = this.brushSize / this.scaleFactor;

      this.saveState();
      console.log("add auto mask:", maskName);
    },
    async removeAutoMask(maskName) {
      const img = await this.loadMaskImage(this.masks[maskName]);

      // 创建临时canvas处理图像
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = this.$refs.canvas.width;
      tempCanvas.height = this.$refs.canvas.height;
      const ctx = tempCanvas.getContext('2d');

      // 步骤1：绘制原始遮罩
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(img, 0, 0);

      // 步骤2：提取目标擦除区域
      const imageData = ctx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
      const data = imageData.data;
      for (let i = 0; i < data.length; i += 4) {
        const isTarget = data[i] > 250 && data[i + 1] > 250 && data[i + 2] > 250;
        data[i + 3] = isTarget ? 255 : 0; // 仅保留目标区域的alpha
      }

      // 步骤3：创建擦除蒙版
      const eraseCanvas = document.createElement('canvas');
      eraseCanvas.width = tempCanvas.width;
      eraseCanvas.height = tempCanvas.height;
      const eraseCtx = eraseCanvas.getContext('2d');
      eraseCtx.putImageData(imageData, 0, 0);

      // 步骤4：执行擦除操作
      this.ctx.globalCompositeOperation = 'destination-out';
      this.ctx.drawImage(eraseCanvas, 0, 0);

      // 重置必要状态
      this.ctx.globalCompositeOperation = 'source-over';
      this.ctx.strokeStyle = '#000';
      this.ctx.lineWidth = this.brushSize / this.scaleFactor;

      this.saveState();
      console.log("remove auto mask:", maskName);
    },
    async updateAllMasks() {
      // 创建临时canvas处理图像
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = this.$refs.canvas.width;
      tempCanvas.height = this.$refs.canvas.height;
      const ctx = tempCanvas.getContext('2d');

      ctx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);

      await Promise.all([...this.selectedMasks].map(name =>
        this.loadMaskImage(this.masks[name])
          .then(img => {
            ctx.globalCompositeOperation = 'source-over';
            ctx.drawImage(img, 0, 0);
          })
      ));

      // 步骤2：创建遮罩专用canvas
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = tempCanvas.width;
      maskCanvas.height = tempCanvas.height;
      const maskCtx = maskCanvas.getContext('2d');

      // 提取白色区域（带抗锯齿处理）
      const imageData = ctx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
      const data = imageData.data;
      for (let i = 0; i < data.length; i += 4) {
        // 放宽白色判断条件（考虑抗锯齿）
        const isWhite = data[i] > 250 && data[i + 1] > 250 && data[i + 2] > 250;
        data[i] = 0;    // R
        data[i + 1] = 0;  // G
        data[i + 2] = 0;  // B
        data[i + 3] = isWhite ? 255 : 0;
      }
      maskCtx.putImageData(imageData, 0, 0);

      // 步骤3：绘制到主画布
      this.ctx.globalCompositeOperation = 'source-over'; // 使用正常混合模式
      this.ctx.drawImage(maskCanvas, 0, 0);

      // 重置必要画布状态
      this.ctx.globalCompositeOperation = 'source-over';
      this.ctx.strokeStyle = '#000';
      this.ctx.lineWidth = this.brushSize / this.scaleFactor;

      this.saveState();
    },
    loadMaskImage(url) {
      url = imageUrl(url, false);
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
      });
    },
  },
  watch: {
    imageUrl(newVal) {
      const img = this.$refs.image
      img.src = ''
      setTimeout(() => img.src = newVal, 0) // 清空缓存强制触发load
    },
    selectedMasks: {
      handler(newVal, oldVal = []) {
        const changes = {
          add: newVal.filter(x => !oldVal.includes(x)),
          remove: oldVal.filter(x => !newVal.includes(x))
        };

        // 先处理移除再处理新增
        changes.remove.forEach(name =>
          this.applyAutoMasks(['remove', name])
        );
        changes.add.forEach(name =>
          this.applyAutoMasks(['add', name])
        );
      },
      deep: true
    }
  }
};
</script>

<style lang="scss" scoped>
.mask-editor {
  .editor-container {
    margin: 4px 0;
    display: flex;
    width: 100%;
    height: auto;
  }

  .image-wrapper {
    display: inline-flex;
    vertical-align: top;
    line-height: 0;
    margin-right: 10px;
    flex: 1;
    position: relative;
    background: #f0f2f5;
    width: calc(100% - 80px);

    .source-image {
      width: 100%;
      height: auto !important;
      object-fit: contain;
      position: relative;
      z-index: 1;
      pointer-events: none;
    }

    canvas {
      position: absolute !important;
      top: 0;
      left: 0;
      z-index: 2;
      width: 100% !important;
      height: auto !important;
      touch-action: none;
      image-rendering: crisp-edges;
      opacity: 0.7;
      mix-blend-mode: multiply;
    }
  }

  .toolbar {
    flex-shrink: 0;
    width: 40px;
    padding: 0 10px;
    border-left: 1px solid #ebeef5;

    .el-row {
      margin-bottom: 5px;

      .el-button {
        padding: 5px;
        font-size: 18px;
      }
    }
  }
}
</style>